<template>

  <v-dialog value="true" :content-class="'app-dialog ' + (isMobileOnly ? 'mobile' : '')" persistent :fullscreen="$vuetify.breakpoint.xsOnly"
    :max-width="maxWidth">

    <v-card class="app-dialog-content">

      <v-toolbar class="app-dialog-header">
        <v-toolbar-title>
          <div>{{ dialogTitle }}</div>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon small @click="cancel">
          <v-icon small>fa-times</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="app-dialog-body">

        <div class="mb-2 text-subtitle-1">
          <div v-if="isDiiaMode">
            <div v-if="isMobile" class="text-center" v-html="getLocale('appSignWithQrCodeDialog.diia.descriptionMobile', {qrCode})"></div>
            <div v-else class="text-center" v-html="getLocale('appSignWithQrCodeDialog.diia.description', {qrCode})"></div>
          </div>
          <div v-else-if="isCloudKeyMode" class="text-center">
            {{ getLocale('appSignWithQrCodeDialog.cloudKey.description') }}
          </div>
          <div v-else-if="isSmartIDMode" class="text-center">
            <div v-html="getLocale('appSignWithQrCodeDialog.smartID.descriptionMobile', { qrCode: encodeURI(qrCode)})"></div>
          </div>
          <div v-else class="text-center">
            {{ getLocale('appSignWithQrCodeDialog.default.description', { mobileAppName: mobileAppName }) }}
          </div>
        </div>

        <app-qr-code :value="qrCode" :size="200" class="d-flex justify-center"></app-qr-code>

        <div class="text-center">
          <div v-if="isQrCodeExpired" class="warning--text my-1">{{ getLocale('appSignWithQrCodeDialog.qrCodeExpired') }}</div>

          <div v-else class="my-1">{{ getLocale('appSignWithQrCodeDialog.qrCodeLifeTime') + `: ${qrCodeLifeTime}` }}</div>

          <v-btn v-if="isDiiaMode && isQrCodeExpired" color="primary" class="" text @click="refreshQr">
            {{ getLocale('appSignWithQrCodeDialog.diia.refreshQr') }}
          </v-btn>
        </div>

      </v-card-text>

      <v-card-actions class="app-dialog-footer d-block elevation-0">
        <v-row no-gutters>
          <v-col cols="12 d-flex">
            <v-spacer></v-spacer>
            <v-btn color="secondary" @click="cancel">
              {{ getLocale('buttons.cancel') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-actions>

    </v-card>

  </v-dialog>

</template>

<script>
import { isMobileOnly, isMobile } from 'mobile-device-detect';

import AppQrCode from '@/components/Common/AppQrCode';
import { EventBus } from '@/helpers/event-bus';
import { getLocale } from '@/helpers/locale-helper';
import { showConfirm } from '@/plugins/dialogs';
import { logger } from '@/helpers/log.service';
import { AppSignKeyOperationType, CloudProvider, PrivateKeyType } from '@/sign/models/sign-constants';

export default {
  name: 'AppSignWithQrCodeDialog',
  components: { AppQrCode },
  props: {
    deepLink: { default: '' },
    deepLinkLifeTimeSeconds: { default: 0 },
    onResult: {
      type: Function,
      default: () => {}
    },
    cloudProvider: { type: String, default: '' },
    privateKeyType: { type: String, default: '' },
    keyOperationType: { type: Number, default: 0 },
    mobileAppName: { type: String, default: '' }
  },
  data() {
    return {
      qrCode: '',
      qrCodeLifeTimeSeconds: -1,
      qrCodeLifeTimeCountDownInterval: null,
      worker: null
    };
  },
  computed: {
    getLocale() { return getLocale; },
    isMobile() { return isMobile; },
    isMobileOnly() { return isMobileOnly; },
    maxWidth() {
      let bp = this.$vuetify.breakpoint;
      return bp.sm ? '80vw' : bp.md ? '70vw' : bp.lg ? '60vw' : '50vw';
    },
    isQrCodeExpired() { return !!this.qrCode && this.qrCodeLifeTimeSeconds === 0; },
    qrCodeLifeTime() {
      let m = 0;
      let s = 0;
      if (this.qrCodeLifeTimeSeconds > 0) {
        if (this.qrCodeLifeTimeSeconds >= 60) {
          m = Math.floor(this.qrCodeLifeTimeSeconds / 60);
        }
        s = this.qrCodeLifeTimeSeconds - m * 60;
      }
      m = `${m}`.padStart(2, '0');
      s = `${s}`.padStart(2, '0');
      return `${m}:${s}`;
    },
    isWorkerSupported() { return !!window.Worker; },
    isDiiaMode() { return this.cloudProvider === CloudProvider.DPDiya; },
    isCloudKeyMode() { return this.cloudProvider === CloudProvider.CloudKey; },
    isSmartIDMode() { return this.cloudProvider === CloudProvider.PrivatSmartId; },
    dialogTitle() {
      let title = '';
      switch (this.cloudProvider) {
        case CloudProvider.CloudKey:
          title = this.keyOperationType === AppSignKeyOperationType.Reading
            ? this.privateKeyType === PrivateKeyType.Seal
              ? 'appSignWithQrCodeDialog.cloudKey.readingSealTitle'
              : 'appSignWithQrCodeDialog.cloudKey.readingTitle'
            : this.privateKeyType === PrivateKeyType.Seal
              ? 'appSignWithQrCodeDialog.cloudKey.signingSealTitle'
              : 'appSignWithQrCodeDialog.cloudKey.signingTitle';
          break;
        case CloudProvider.PrivatSmartId:
          title = this.keyOperationType === AppSignKeyOperationType.Reading
            ? this.privateKeyType === PrivateKeyType.Seal
              ? 'appSignWithQrCodeDialog.smartID.readingSealTitle'
              : 'appSignWithQrCodeDialog.smartID.readingTitle'
            : this.privateKeyType === PrivateKeyType.Seal
              ? 'appSignWithQrCodeDialog.smartID.signingSealTitle'
              : 'appSignWithQrCodeDialog.smartID.signingTitle';
          break;
        case CloudProvider.DPDiya:
          title = this.privateKeyType === PrivateKeyType.Seal
            ? 'appSignWithQrCodeDialog.diia.titleSeal'
            : 'appSignWithQrCodeDialog.diia.title';
          break;
        default:
          title = this.keyOperationType === AppSignKeyOperationType.Reading
            ? this.privateKeyType === PrivateKeyType.Seal
              ? 'appSignWithQrCodeDialog.default.readingSealTitle'
              : 'appSignWithQrCodeDialog.default.readingTitle'
            : this.privateKeyType === PrivateKeyType.Seal
              ? 'appSignWithQrCodeDialog.default.signingSealTitle'
              : 'appSignWithQrCodeDialog.default.signingTitle';
          break;
      }
      return title ? getLocale(title) : title;
    }
  },
  mounted() {
    logger.debug(`[AppSignWithQrCodeDialog:mounted] cloudProvider=${this.cloudProvider} privateKeyType=${this.privateKeyType} keyOperationType=${this.keyOperationType} mobileAppName=${this.mobileAppName} deepLink=${this.deepLink}`);

    this.subscribe();
    this.qrCode = this.deepLink;
  },
  beforeDestroy() {
    this.unsubscribe();
    clearInterval(this.qrCodeLifeTimeCountDownInterval);
    if (this.worker) {
      this.worker.postMessage(['stopInterval']);
    }
  },
  methods: {
    async beginQrCodeLifeTimeCountDown() {
      await this.initWorker();

      this.qrCodeLifeTimeSeconds = this.deepLinkLifeTimeSeconds - 1;

      if (this.qrCodeLifeTimeSeconds > 0) {
        if (this.worker) {
          this.worker.onmessage = () => {
            this.qrCodeLifeTimeSeconds -= 1;
            if (this.qrCodeLifeTimeSeconds <= 0) {
              this.worker.postMessage(['stopInterval']);
            }
          };
          logger.debug(`[AppSignWithQrCodeDialog:beginQrCodeLifeTimeCountDown] counting with worker`);
          this.worker.postMessage(['startInterval', 1000]);
        } else {
          logger.debug(`[AppSignWithQrCodeDialog:beginQrCodeLifeTimeCountDown] counting with setInterval`);
          this.qrCodeLifeTimeCountDownInterval = setInterval(() => {
            this.qrCodeLifeTimeSeconds -= 1;
            if (this.qrCodeLifeTimeSeconds <= 0) {
              clearInterval(this.qrCodeLifeTimeCountDownInterval);
            }
          }, 1000);
        }
      }
    },
    async cancel() {
      let confirmed = await showConfirm('appSignWithQrCodeDialog.confirmCancel');
      if (confirmed) {
        EventBus.$emit('app-sign-with-qrcode-dlg-cancelled');
      }
    },
    async initWorker() {
      if (this.isWorkerSupported && !this.worker) {
        const TimersWorker = (
          await import('worker-loader!../../workers/timers-worker.js')
        ).default;
        this.worker = new TimersWorker();
      }
    },
    onClose() {
      this.$destroy();
    },
    onQrCodeChange(prms) {
      this.qrCode = prms ? prms.deepLink : '';
    },
    refreshQr() {
      EventBus.$emit('app-sign-with-qrcode-dlg-refresh-qr-code');
    },
    subscribe() {
      EventBus.$on('app-sign-with-qrcode-dlg-close', this.onClose);
      EventBus.$on('app-sign-with-qrcode-dlg-new-qr-code', this.onQrCodeChange);
    },
    unsubscribe() {
      EventBus.$off('app-sign-with-qrcode-dlg-close', this.onClose);
      EventBus.$off('app-sign-with-qrcode-dlg-new-qr-code', this.onQrCodeChange);
    }
  },
  watch: {
    qrCode(newVal, oldVal) {
      if (newVal && (!oldVal || newVal !== oldVal)) {
        this.beginQrCodeLifeTimeCountDown();
      }
    }
  }
};
</script>
